import type { HgSupportedLanguage } from '@smg-real-estate/domus';

import { isSameAgencySearch } from '@/helpers/AgencyDirectory/agencyRouteParams';
import { assertCanonicalDirectoryLocationSlug } from '@/helpers/Directory/directoryCanonicalRedirect';
import { assertDirectoryLocationsResolved } from '@/helpers/Directory/directoryLocationValidation';
import { useAgencyProfilesStore } from '@/stores/agencyProfiles';
import { resolveGeoLocations } from '@/stores/geoLocationHelpers';
import { LoadState } from '@/stores/types';
import type { ListingType, SortingOption } from '@/types';

export async function syncAgencyDirectoryFromRoute({
  geoLocations,
  locale,
  currentPage,
  includeChildren,
  listingType,
  sortBy,
}: {
  geoLocations: string[];
  locale: HgSupportedLanguage;
  currentPage: number;
  includeChildren: boolean;
  listingType: ListingType;
  sortBy: SortingOption;
}): Promise<boolean> {
  const store = useAgencyProfilesStore();
  const generation = store.beginAgencyRouteSync();
  const resolvedLocations = await resolveGeoLocations(geoLocations, locale);

  if (generation !== store.agenciesRequestGeneration) {
    return false;
  }

  assertDirectoryLocationsResolved(geoLocations, resolvedLocations, locale);
  assertCanonicalDirectoryLocationSlug({
    requestedPrimarySlug: geoLocations[0],
    resolvedLocations,
    locale,
    directoryType: 'agency',
    page: currentPage,
  });

  if (
    store.agenciesLoadState === LoadState.LOADED &&
    isSameAgencySearch({
      resolvedLocations,
      currentPage,
      searchedLocations: store.searchedLocations,
      loadedPage: store.agencyListCurrentPage,
      includeChildren,
      loadedIncludeChildren: store.loadedIncludeChildrenAgencies,
      listingType,
      loadedListingType: store.loadedListingType,
      sortBy,
      loadedSortBy: store.loadedSortingOption,
    })
  ) {
    store.setRouteResolvedLocations(resolvedLocations);
    return false;
  }

  await store.changeUrlParams({
    geoLocations,
    locale,
    includeChildren,
    currentPage,
    listingType,
    sortBy,
    requestGeneration: generation,
    resolvedLocations,
  });

  return true;
}
